'use strict';
//***********************************************************************************
//***********************************************************************************
//**** Build nomenclatures
//***********************************************************************************
//***********************************************************************************

import { cn_azimut, cn_mul } from '../cn_utilities';
import {
    CLOSING_LIST,
    code_to_label,
    GLAZING_GAZ_LIST,
    OPENING_LIST,
    SILL_LIST,
    TRANSOM_LIST,
    VERTICAL_OPENING_LIST,
} from '../../model/cn_opening_type';
import { cn_building } from '../../model/cn_building';
import { ROOF_GLAZING_LIST, ROOF_OPENING_CATEGORY_LIST } from '../../model/cn_roof_opening_type';
import { FENCE_LOCK_LIST, FENCE_MATERIAL_LIST, FENCE_OPENING_LIST } from '../../model/cn_fence_opening_type';
import { cn_storey, STOREY_EXTERIOR_LABEL } from '../../model/cn_storey';
import { cn_nomenclature, export_layers_and_zpsos, find_zone, push_cn_nomenclatures_zones } from '../cn_nomenclature';
import { extension_instance } from '../../extension/cn_extension';
import { cn_zone } from '../../model/cn_zone';
import { cn_plugin_option } from '../cn_plugin_option';
import { cn_constant, FRAME_MATERIALS, GLAZING_LIST } from '../../model/cn_model_constants';


//***********************************************************************************
//***********************************************************************************
/**
 * Buils opening and roof openings nomenclatures for the building
 * @param {cn_building} building
 * @param is_exterior
 * @returns {cn_nomenclature[]}
 */

export function cn_nomenclature_openings(building, is_exterior = false) {
    const output = [];
    output.push(new cn_nomenclature('ID').withVirtual(true));
    output.push(new cn_nomenclature('Nom'));
    output.push(new cn_nomenclature('Identifiant'));
    output.push(new cn_nomenclature('Type'));
    if (!is_exterior) {
        output.push(new cn_nomenclature('Niveau'));
        output.push(new cn_nomenclature('Index niveau').withVisibility(false));
    }
    push_cn_nomenclatures_zones(output, true);
    output.push(new cn_nomenclature('Espace 1'));
    output.push(new cn_nomenclature('Espace 2'));
    output.push(new cn_nomenclature('Orientation').withNumberDefinition('°', 0));
    if (!is_exterior) {
        output.push(new cn_nomenclature('Matériau dormant'));
        if (cn_plugin_option.second_opening_frame)
            output.push(new cn_nomenclature('Matériau second dormant'));
        output.push(new cn_nomenclature('Matériau ouvrant'));
        output.push(new cn_nomenclature('Vitrage'));
    } else {
        output.push(new cn_nomenclature('Matériau'));
        output.push(new cn_nomenclature('Serrure'));
    }
    output.push(new cn_nomenclature('Ouverture'));
    output.push(new cn_nomenclature('Panneaux'));
    if (!is_exterior) {
        output.push(new cn_nomenclature('Imposte'));
        output.push(new cn_nomenclature('Matériau imposte'));
        output.push(new cn_nomenclature('Vitrage imposte'));
        output.push(new cn_nomenclature('Allège'));
        output.push(new cn_nomenclature('Fermeture'));
    }
    output.push(new cn_nomenclature('Largeur').withNumberDefinition('m', 2));
    output.push(new cn_nomenclature('Hauteur').withNumberDefinition('m', 2));
    output.push(new cn_nomenclature('Épaisseur').withNumberDefinition('m', 2));
    output.push(new cn_nomenclature('Décalage').withNumberDefinition('m', 2));
    if (!is_exterior) {
        output.push(new cn_nomenclature('Hauteur d\'imposte').withNumberDefinition('m', 2));
        output.push(new cn_nomenclature('Hauteur d\'allège').withNumberDefinition('m', 2));
    }
    output.push(new cn_nomenclature('Surface').withNumberDefinition('m²', 2));
    output.push(new cn_nomenclature('Surface vitrée').withNumberDefinition('m²', 2));
    if (!is_exterior) {
        output.push(new cn_nomenclature('Uw').withNumberDefinition('W/m².K', 3));
        output.push(new cn_nomenclature('Facteur solaire').withNumberDefinition('%', 2));
        output.push(new cn_nomenclature('Transmittance').withNumberDefinition('%', 2));
        output.push(new cn_nomenclature('Périmètre de tableau').withNumberDefinition('m', 2));
        output.push(new cn_nomenclature('Profondeur de tableau').withNumberDefinition('m', 2));
    }
    output.push(new cn_nomenclature('Calques').withVisibility(false));
    if (cn_plugin_option.zpsos) {
        output.push(new cn_nomenclature('ZPSOs').withVisibility(false));
    }

    const zones = building.zones;

    building.element_types.forEach(et => {
        if (et.compute_physics) et.compute_physics();
    })

    /*** exterior */
    if (is_exterior) {
        populate_nomenclature_openings(output, zones, building.exterior, is_exterior);
    } else {
        /** interior */
        building.storeys.length && building.storeys.forEach((storey) => {
            /*** openings */
            populate_nomenclature_openings(output, zones, storey, is_exterior);
            //*** roof openings */
            populate_nomenclature_roof_openings(output, zones, storey);
        });
    }
    return output;
}

//***********************************************************************************
//***********************************************************************************
/**
 /**
 * Builds openings nomenclatures for the given storey
 * @param {any[]} output
 * @param {{[property: string]: cn_zone[]}} zones
 * @param {cn_storey} storey
 */
function populate_nomenclature_openings(output, zones, storey, is_exterior = false) {
    storey.scene.walls.length && storey.scene.walls.forEach((wall) => {
        wall.openings.length && wall.openings.filter(opening => opening.valid).forEach((opening) => {
            const type = opening.get_opening_type();
            const wall_interface = opening.wall_interface;
            let k = 0;

            //*** ID */
            output[k].values.push(opening.create_id_for_bbp(storey));
            k++

            //*** name */
            output[k].values.push(type.get_label());
            k++

            //*** identifyer */
            output[k].values.push(opening.get_readable_id(storey));
            k++

            //*** Type */
            let tp = (type.category === 'window') ? 'Fenêtre' : 'Porte';
            if (type.free) tp += ' libre';
            output[k].values.push(tp);
            k++

            if (!is_exterior) {
                //*** storey */
                output[k].values.push(storey.get_storey_name());
                k++
                output[k].values.push(storey.get_storey_index_in_building());
                k++
            }

            const s0 = (wall_interface.get_flow_direction()) ? 0 : 1;
            const s1 = 1 - s0;

            //*** Zone */
            extension_instance.zone.get_zones_tools().forEach(tool_zone => {
                let zone_to_display = find_zone(zones[tool_zone.property], wall_interface.spaces[s0].ID, storey.ID);
                if (!zone_to_display) {
                    zone_to_display = find_zone(zones[tool_zone.property], wall_interface.spaces[s1].ID, storey.ID);
                }
                output[k].values.push(zone_to_display);
                k++;
            });

            //*** Space 1 */
            output[k].values.push(wall_interface.spaces[s0].get_name(storey));
            k++

            //*** Space 2 */
            output[k].values.push(wall_interface.spaces[s1].get_name(storey));
            k++

            //*** Orientation */
            const flow_normal = cn_mul(wall.bounds.normal, (s0 == 0) ? 1 : -1);
            output[k].values.push(cn_azimut(flow_normal, storey.building.compass_orientation));
            k++

            if (type.free) {
                if (!is_exterior) {
                    //*** Frame  */
                    output[k].values.push(type.get_frame_label());
                    k++

                    //*** second Frame  */
                    if (cn_plugin_option.second_opening_frame) {
                        output[k].values.push('');
                        k++
                    }

                    //*** opening frame  */
                    output[k].values.push('');
                    k++

                    //*** Glazing  */
                    output[k].values.push('');
                    k++

                    //*** Opening  */
                    output[k].values.push('');
                    k++

                    //*** Panels  */
                    output[k].values.push(0);
                    k++

                    //*** Transom  */
                    output[k].values.push('');
                    k++

                    //*** transom frame  */
                    output[k].values.push('');
                    k++

                    //*** transom glazing  */
                    output[k].values.push('');
                    k++

                    //*** Sill  */
                    output[k].values.push('');
                    k++

                    //*** Closing  */
                    output[k].values.push('');
                    k++
                } else {
                    //*** Materiau  */
                    output[k].values.push('');
                    k++

                    //*** Serrure  */
                    output[k].values.push('');
                    k++

                    //*** Opening  */
                    output[k].values.push('');
                    k++

                    //*** Panels  */
                    output[k].values.push(0);
                    k++
                }
            } else {
                if (!is_exterior) {
                    //*** Frame  */
                    output[k].values.push(type.get_frame_label());
                    k++

                    //*** second Frame  */
                    if (cn_plugin_option.second_opening_frame) {
                        output[k].values.push(type.get_second_frame_label());
                        k++
                    }

                    //*** opening Frame  */
                    output[k].values.push(type.get_sash_frame_label());
                    k++

                    //*** Glazing  */
                    var glazing = cn_constant.code_to_label(type.glazing, GLAZING_LIST);
                    if (type.glazing == 'double') glazing += ' / ' + code_to_label(type.glazing_gaz, GLAZING_GAZ_LIST);
                    output[k].values.push(glazing);
                    k++

                    //*** Opening  */
                    var opening_opening = code_to_label(type.opening, OPENING_LIST);
                    if (type.opening == 'vertical') opening_opening += ' / ' + code_to_label(type.vertical_opening, VERTICAL_OPENING_LIST);
                    output[k].values.push(opening_opening);
                    k++
                } else {
                    //*** Materiau  */
                    output[k].values.push(code_to_label(type.material, FENCE_MATERIAL_LIST));
                    k++

                    //*** Serrure  */
                    output[k].values.push(code_to_label(type.lock, FENCE_LOCK_LIST));
                    k++

                    //*** Opening  */
                    output[k].values.push(code_to_label(type.opening, FENCE_OPENING_LIST));
                    k++
                }

                //*** Panels  */
                output[k].values.push(type.panels);
                k++

                if (!is_exterior) {
                    //*** Transom  */
                    output[k].values.push(code_to_label(type.transom, TRANSOM_LIST));
                    k++

                    //*** transom Frame  */
                    output[k].values.push(type.get_transom_frame_label());
                    k++

                    //*** transom glazing  */
                    output[k].values.push(type.get_transom_glazing_label());
                    k++

                    //*** Sill  */
                    output[k].values.push(code_to_label(type.sill, SILL_LIST));
                    k++

                    //*** Closing  */
                    output[k].values.push(code_to_label(type.closing, CLOSING_LIST));
                    k++
                }
            }

            //*** Width  */
            output[k].values.push(type.width);
            k++

            //*** height  */
            output[k].values.push(type.height);
            k++

            //*** thickness  */
            output[k].values.push(opening.thickness);
            k++

            //*** z  */
            output[k].values.push(type.z);
            k++

            if (!is_exterior) {
                //*** transom_height  */
                output[k].values.push((!type.free && type.transom === 'none') ? 0 : Number(type.transom_height));
                k++

                //*** sill height  */
                output[k].values.push((!type.free && type.sill === 'none') ? 0 : Number(type.sill_height));
                k++
            }

            //*** Area  */
            output[k].values.push(type.full_area);
            k++

            //*** Glazing Area  */
            output[k].values.push(type.glazing_area);
            k++

            if (!is_exterior) {
                if (type.free) {
                    //*** Uw  */
                    output[k].values.push('');
                    k++

                    //*** Sw  */
                    output[k].values.push('');
                    k++

                    //*** Tl  */
                    output[k].values.push('');
                    k++
                } else {
                    //*** Uw  */
                    output[k].values.push(type.Uw.toFixed(3));
                    k++

                    //*** Sw  */
                    output[k].values.push(type.Sw.toFixed(3));
                    k++

                    //*** Tl  */
                    output[k].values.push(type.Tl.toFixed(3));
                    k++
                }

                //*** Frame perimeter */
                output[k].values.push(type.get_perimeter() - type.width);
                k++

                //*** frame depth */
                output[k].values.push(opening.get_outside_depth());
                k++
            }

            //*** layers & zpsos  */
            k = export_layers_and_zpsos(storey, opening, type, output, k);

        });
    });
}

/**
 * Builds roof openings nomenclatures for the given storey
 * @param {any[]} output
 * @param {{[property: string]: cn_zone[]}} zones
 * @param {cn_storey} storey
 */
function populate_nomenclature_roof_openings(output, zones, storey) {
    if (storey.roof) {
        const roof = storey.roof;
        roof.openings.length && roof.openings.forEach((roof_opening) => {
            const type = roof_opening.get_opening_type();
            let k = 0;

            //*** ID */
            output[k].values.push(roof_opening.create_id_for_bbp(storey));
            k++

            //*** Name */
            output[k].values.push(type.get_label());
            k++

            //*** identifyer */
            output[k].values.push(roof_opening.get_readable_id(storey));
            k++

            //*** Type */
            output[k].values.push(code_to_label(type.category, ROOF_OPENING_CATEGORY_LIST));
            k++

            //*** Storey */
            output[k].values.push(storey.get_storey_name());
            k++

            output[k].values.push(storey.get_storey_index_in_building());
            k++

            //*** Zone */
            const space = storey.scene.find_space(roof_opening.position)
            extension_instance.zone.get_zones_tools().forEach(tool_zone => {
                const zone_to_display = (space) ? find_zone(zones[tool_zone.property], space.ID, storey.ID) : null;
                output[k].values.push(zone_to_display);
                k++;
            });

            //*** Space 1 */
            if (space) {
                output[k].values.push(space.get_name(storey));
            } else {
                output[k].values.push('');
            }
            k++

            //*** Space 2 */
            output[k].values.push('');
            k++

            //*** Orientation */
            output[k].values.push('');
            k++

            //*** Frame */
            output[k].values.push(cn_constant.code_to_label(type.frame, FRAME_MATERIALS));
            k++

            //*** Second Frame */
            if (cn_plugin_option.second_opening_frame) {
                output[k].values.push('');
                k++
            }

            output[k].values.push('');
            k++

            //*** Glazing */
            var glazing = code_to_label(type.glazing, ROOF_GLAZING_LIST);
            if (type.glazing == 'double') glazing += ' / ' + code_to_label(type.glazing_gaz, GLAZING_GAZ_LIST);
            output[k].values.push(glazing);
            k++

            //*** Opening */
            output[k].values.push(code_to_label(type.opening, OPENING_LIST));
            k++

            //*** Panels  */
            output[k].values.push(1);
            k++

            //*** Transom  */
            output[k].values.push('');
            k++

            //*** Transom material */
            output[k].values.push('');
            k++

            //*** Transom glazing */
            output[k].values.push('');
            k++

            //*** Sill  */
            output[k].values.push('');
            k++

            //*** Closing */
            output[k].values.push(code_to_label(type.closing, CLOSING_LIST));
            k++

            //*** Width */
            output[k].values.push(type.width);
            k++

            //*** Height */
            output[k].values.push(type.length);
            k++

            //*** thickness */
            output[k].values.push('');
            k++

            //*** z */
            output[k].values.push('');
            k++

            //*** transom_height */
            output[k].values.push('');
            k++

            //*** sill height */
            output[k].values.push('');
            k++

            //*** Area */
            output[k].values.push(type.get_area());
            k++

            //*** Glazing Area */
            output[k].values.push(type.get_glazing_area());
            k++

            //*** Uw  */
            output[k].values.push(type.Uw.toFixed(3));
            k++

            //*** Sw  */
            output[k].values.push(type.Sw.toFixed(3));
            k++

            //*** Tl  */
            output[k].values.push(type.Tl.toFixed(3));
            k++

            //*** Frame perimeter  */
            output[k].values.push(2 * (type.width + type.length));
            k++

            //*** Frame depth  */
            output[k].values.push('');
            k++

            //*** layers & zpsos  */
            k = export_layers_and_zpsos(storey, roof_opening, type, output, k);
        });
    }
}
